import React, { useState, useEffect, useRef } from 'react';
import './Business.css';
import UnderConstructionNotice from '../components/UnderConstructionNotice.jsx';
import { getStudentData } from '../assets/data';
import { getLessonsFromLocalStorage } from '../utils/lessonsService';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import businessBanner from '../assets/BusinessAnalytics.jpg';
import novasLogo from '../assets/novas-logo.png';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function Business() {
  // eslint-disable-next-line no-unused-vars
  const [students, setStudents] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [lessons, setLessons] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalRevenue: 0,
    activeStudents: 0,
    completedLessons: 0,
    averagePrice: 0,
    scheduleUtilization: 0,
    monthlyGrowth: 0,
    retentionRate: 0,
    averageProgress: 0,
    topStudents: [],
    atRiskStudents: [],
    studentSegments: {},
    engagementMetrics: {}
  });
  const [chartData, setChartData] = useState({
    revenueChart: null,
    lessonsChart: null,
    studentsChart: null
  });
  const [studentAnalytics, setStudentAnalytics] = useState({
    retentionRate: 0,
    averageProgress: 0,
    topStudents: [],
    atRiskStudents: [],
    studentSegments: {},
    engagementMetrics: {}
  });

  const [aiInsights, setAiInsights] = useState({
    recommendations: [],
    alerts: [],
    opportunities: [],
    trends: []
  });

  const [bannerImage, setBannerImage] = useState(businessBanner);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = () => {
    console.log('🔄 Loading Business data...');
    
    // Загружаем данные студентов
    const studentData = getStudentData();
    console.log('👥 Students data:', studentData);
    setStudents(studentData);

    // Загружаем уроки из localStorage
    const lessonsData = getLessonsFromLocalStorage();
    console.log('📚 Lessons data:', lessonsData);
    setLessons(lessonsData);

    // Рассчитываем аналитику
    calculateAnalytics(studentData, lessonsData);

    // Генерация данных для графиков
    generateChartData(studentData, lessonsData);
    
    // Аналитика студентов
    calculateStudentAnalytics(studentData, lessonsData);
  };

  const calculateAnalytics = (studentData, lessonsData) => {
    console.log('📊 Calculating analytics...');
    
    const now = new Date();
    // eslint-disable-next-line no-unused-vars
    const currentMonth = now.getMonth();
    // eslint-disable-next-line no-unused-vars
    const currentYear = now.getFullYear();

    // Активные студенты
    const activeStudents = Object.values(studentData).filter(student => 
      student.status === 'Active' || !student.status
    ).length;
    console.log('✅ Active students:', activeStudents);

    // Завершенные уроки
    const completedLessons = lessonsData.filter(lesson => 
      lesson.status === 'done'
    ).length;
    console.log('✅ Completed lessons:', completedLessons);

    // Доходы (базовая логика - можно расширить)
    const totalRevenue = Object.values(studentData).reduce((sum, student) => {
      const studentLessons = lessonsData.filter(lesson => 
        lesson.studentId === Object.keys(studentData).find(key => studentData[key] === student) &&
        lesson.status === 'done'
      );
      const price = parseFloat(student.price?.replace('$', '').replace('/h', '') || 40);
      return sum + (studentLessons.length * price);
    }, 0);
    console.log('✅ Total revenue:', totalRevenue);

    // Средняя цена
    const averagePrice = completedLessons > 0 ? totalRevenue / completedLessons : 0;
    console.log('✅ Average price:', averagePrice);

    // Загруженность расписания (упрощенная логика)
    const scheduleUtilization = Math.min(85, 60 + Math.random() * 30); // Временно

    // Рост по сравнению с прошлым месяцем
    const monthlyGrowth = Math.round((Math.random() - 0.3) * 40); // Временно

    const finalAnalytics = {
      totalRevenue: Math.round(totalRevenue),
      activeStudents,
      completedLessons,
      averagePrice: Math.round(averagePrice),
      scheduleUtilization: Math.round(scheduleUtilization),
      monthlyGrowth
    };
    
    console.log('📊 Final analytics:', finalAnalytics);
    setAnalytics(finalAnalytics);

    // Generate AI insights after analytics are loaded
    generateAIInsights(finalAnalytics);
  };

  // Генерация данных для графиков
  const generateChartData = (studentData, lessonsData) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonth = new Date().getMonth();
    
    // Данные доходов за последние 6 месяцев
    const revenueData = [];
    const lessonsChartData = [];
    const studentsChartData = [];
    
    for (let i = 5; i >= 0; i--) {
      const monthIndex = (currentMonth - i + 12) % 12;
      const monthName = months[monthIndex];
      
      // Генерируем реалистичные данные (можно заменить на реальные)
      const baseRevenue = 2000 + Math.random() * 1000;
      const baseLessons = 30 + Math.random() * 20;
      const baseStudents = 6 + Math.random() * 4;
      
      revenueData.push({
        month: monthName,
        value: Math.round(baseRevenue)
      });
      
      lessonsChartData.push({
        month: monthName,
        value: Math.round(baseLessons)
      });
      
      studentsChartData.push({
        month: monthName,
        value: Math.round(baseStudents)
      });
    }

    // График доходов
    const revenueChart = {
      labels: revenueData.map(d => d.month),
      datasets: [
        {
          label: 'Revenue',
          data: revenueData.map(d => d.value),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: 'rgb(34, 197, 94)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
        }
      ]
    };

    // График уроков
    const lessonsChart = {
      labels: lessonsChartData.map(d => d.month),
      datasets: [
        {
          label: 'Completed Lessons',
          data: lessonsChartData.map(d => d.value),
          backgroundColor: 'rgba(139, 92, 246, 0.8)',
          borderColor: 'rgb(139, 92, 246)',
          borderWidth: 2,
          borderRadius: 8,
        }
      ]
    };

    // График студентов
    const studentsChart = {
      labels: studentsChartData.map(d => d.month),
      datasets: [
        {
          label: 'Active Students',
          data: studentsChartData.map(d => d.value),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: 'rgb(59, 130, 246)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
        }
      ]
    };

    setChartData({
      revenueChart,
      lessonsChart,
      studentsChart
    });
  };

  const calculateStudentAnalytics = (studentData, lessonsData) => {
    console.log('👥 Calculating student analytics...');
    
    const now = new Date();
    const threeMonthsAgo = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));
    
    // Анализ удержания студентов
    const studentRetention = Object.entries(studentData).map(([studentId, student]) => {
      const studentLessons = lessonsData.filter(lesson => 
        lesson.studentId === studentId && lesson.status === 'done'
      );
      
      const recentLessons = studentLessons.filter(lesson => {
        const lessonDate = new Date(lesson.date);
        return lessonDate >= threeMonthsAgo;
      });
      
      const totalLessons = studentLessons.length;
      const recentActivity = recentLessons.length;
      const lastLessonDate = studentLessons.length > 0 ? 
        new Date(studentLessons[studentLessons.length - 1].date) : null;
      
      const daysSinceLastLesson = lastLessonDate ? 
        Math.floor((now - lastLessonDate) / (1000 * 60 * 60 * 24)) : 999;
      
      // Определяем статус студента
      let status = 'active';
      if (student.status === 'Inactive' || student.status === 'Paused') {
        status = 'inactive';
      } else if (daysSinceLastLesson > 30) {
        status = 'at_risk';
      } else if (daysSinceLastLesson > 14) {
        status = 'needs_attention';
      }
      
      return {
        id: studentId,
        name: student.name,
        status: status,
        totalLessons,
        recentActivity,
        daysSinceLastLesson,
        price: parseFloat(student.price?.replace('$', '').replace('/h', '') || 40),
        proficiency: student.proficiency,
        lastLessonDate
      };
    });
    
    // Расчет удержания
    const activeStudents = studentRetention.filter(s => s.status === 'active').length;
    const totalStudents = studentRetention.length;
    const retentionRate = totalStudents > 0 ? (activeStudents / totalStudents) * 100 : 0;
    
    // Топ студентов по активности
    const topStudents = studentRetention
      .filter(s => s.status === 'active')
      .sort((a, b) => b.recentActivity - a.recentActivity)
      .slice(0, 3);
    
    // Студенты в группе риска
    const atRiskStudents = studentRetention
      .filter(s => s.status === 'at_risk' || s.status === 'needs_attention')
      .sort((a, b) => b.daysSinceLastLesson - a.daysSinceLastLesson);
    
    // Сегментация по уровням
    const studentSegments = studentRetention.reduce((segments, student) => {
      const level = student.proficiency?.split('–')[0]?.trim() || 'Unknown';
      if (!segments[level]) {
        segments[level] = { count: 0, active: 0, revenue: 0 };
      }
      segments[level].count++;
      if (student.status === 'active') {
        segments[level].active++;
      }
      segments[level].revenue += student.totalLessons * student.price;
      return segments;
    }, {});
    
    // Метрики вовлеченности
    const engagementMetrics = {
      averageLessonsPerStudent: totalStudents > 0 ? 
        studentRetention.reduce((sum, s) => sum + s.totalLessons, 0) / totalStudents : 0,
      averageDaysBetweenLessons: 7, // Упрощенная логика
      completionRate: 85, // Упрощенная логика
      satisfactionScore: 4.2 // Упрощенная логика
    };
    
    console.log('✅ Student analytics calculated:', {
      retentionRate,
      topStudents,
      atRiskStudents,
      studentSegments,
      engagementMetrics
    });
    
    setStudentAnalytics({
      retentionRate: Math.round(retentionRate),
      averageProgress: Math.round(engagementMetrics.averageLessonsPerStudent),
      topStudents,
      atRiskStudents,
      studentSegments,
      engagementMetrics
    });
  };

  const generateAIInsights = (analyticsData) => {
    // Simulate AI analysis based on current data
    const recommendations = [
      {
        type: 'revenue',
        title: 'Increase average lesson price',
        description: 'Your average lesson price is below market rate. We recommend increasing by 15-20%.',
        impact: 'high',
        action: 'Raise prices'
      },
      {
        type: 'retention',
        title: 'Improve student retention',
        description: 'Retention rate can be improved with personalized approach.',
        impact: 'medium',
        action: 'Implement notification system'
      },
      {
        type: 'schedule',
        title: 'Optimize schedule',
        description: 'Schedule utilization can be increased by 25%.',
        impact: 'high',
        action: 'Add new time slots'
      }
    ];

    const alerts = [
      {
        type: 'warning',
        title: 'Students at risk of leaving',
        description: `${analyticsData?.atRiskStudents?.length || 0} students haven't attended lessons for over 2 weeks`,
        priority: 'high'
      },
      {
        type: 'info',
        title: 'Low utilization',
        description: 'Schedule is only 60% filled',
        priority: 'medium'
      }
    ];

    const opportunities = [
      {
        title: 'Service expansion',
        description: 'Add group classes to increase revenue',
        potential: '30% revenue growth'
      },
      {
        title: 'Marketing',
        description: 'Launch referral program to attract new students',
        potential: '15 new students'
      },
      {
        title: 'Optimization',
        description: 'Automate lesson scheduling',
        potential: 'Save 5 hours per week'
      }
    ];

    const trends = [
      {
        title: 'Revenue growth',
        trend: 'up',
        value: '+12%',
        period: 'this month'
      },
      {
        title: 'New students',
        trend: 'up',
        value: '+3',
        period: 'this week'
      },
      {
        title: 'Completed lessons',
        trend: 'up',
        value: '+8%',
        period: 'this month'
      }
    ];

    setAiInsights({
      recommendations,
      alerts,
      opportunities,
      trends
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Настройки для графиков
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function(context) {
            if (context.dataset.label === 'Revenue') {
              return `Revenue: ${formatCurrency(context.parsed.y)}`;
            }
            return `${context.dataset.label}: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#64748b',
          font: {
            size: 12,
          }
        }
      },
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          color: '#64748b',
          font: {
            size: 12,
          },
          callback: function(value) {
            if (this.chart.data.datasets[0].label === 'Revenue') {
              return formatCurrency(value);
            }
            return value;
          }
        }
      }
    }
  };

  const barChartOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        beginAtZero: true
      }
    }
  };

  const handleBannerUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleBannerFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="business-container">
      <UnderConstructionNotice section="Business" />
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleBannerFileChange}
        accept="image/*"
      />
      <div
        className="business-banner"
        style={{ backgroundImage: `url(${bannerImage})` }}
        onClick={handleBannerUploadClick}
      >
        <img src={novasLogo} alt="NOVAS Logo" className="business-logo" />
        <div className="banner-content">
          <h1>Business Analytics</h1>
          <p>Manage your educational business with data-driven insights.</p>
        </div>
        <div className="business-banner-upload-overlay">Click to change banner</div>
      </div>

      {/* Step 1: Key Performance Indicators */}
      <div className="kpi-grid">
        <div className="kpi-card revenue">
          <div className="kpi-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          </div>
          <div className="kpi-content">
            <h3>Total Revenue</h3>
            <div className="kpi-value">{formatCurrency(analytics.totalRevenue)}</div>
            <div className={`kpi-change ${analytics.monthlyGrowth >= 0 ? 'positive' : 'negative'}`}>
              {analytics.monthlyGrowth >= 0 ? '+' : ''}{analytics.monthlyGrowth}% vs last month
            </div>
          </div>
        </div>

        <div className="kpi-card students">
          <div className="kpi-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div className="kpi-content">
            <h3>Active Students</h3>
            <div className="kpi-value">{analytics.activeStudents}</div>
            <div className="kpi-subtitle">Currently enrolled</div>
          </div>
        </div>

        <div className="kpi-card lessons">
          <div className="kpi-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10,9 9,9 8,9"/>
            </svg>
          </div>
          <div className="kpi-content">
            <h3>Completed Lessons</h3>
            <div className="kpi-value">{analytics.completedLessons}</div>
            <div className="kpi-subtitle">This month</div>
          </div>
        </div>

        <div className="kpi-card average">
          <div className="kpi-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="8" y1="6" x2="21" y2="6"/>
              <line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <line x1="3" y1="6" x2="3.01" y2="6"/>
              <line x1="3" y1="12" x2="3.01" y2="12"/>
              <line x1="3" y1="18" x2="3.01" y2="18"/>
            </svg>
          </div>
          <div className="kpi-content">
            <h3>Average Price</h3>
            <div className="kpi-value">{formatCurrency(analytics.averagePrice)}</div>
            <div className="kpi-subtitle">Per lesson</div>
          </div>
        </div>

        <div className="kpi-card utilization">
          <div className="kpi-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </div>
          <div className="kpi-content">
            <h3>Schedule Utilization</h3>
            <div className="kpi-value">{analytics.scheduleUtilization}%</div>
            <div className="kpi-subtitle">Optimal: 80-90%</div>
          </div>
        </div>

        <div className="kpi-card efficiency">
          <div className="kpi-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
          </div>
          <div className="kpi-content">
            <h3>Business Health</h3>
            <div className="kpi-value">Good</div>
            <div className="kpi-subtitle">Based on metrics</div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-grid">
        <div className="chart-card">
          <div className="chart-header">
            <h3>Revenue Trends</h3>
            <p>Monthly revenue over the last 6 months</p>
          </div>
          <div className="chart-container">
            {chartData.revenueChart && (
              <Line data={chartData.revenueChart} options={chartOptions} />
            )}
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>Lessons Completed</h3>
            <p>Number of completed lessons per month</p>
          </div>
          <div className="chart-container">
            {chartData.lessonsChart && (
              <Bar data={chartData.lessonsChart} options={barChartOptions} />
            )}
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>Student Growth</h3>
            <p>Active students over time</p>
          </div>
          <div className="chart-container">
            {chartData.studentsChart && (
              <Line data={chartData.studentsChart} options={chartOptions} />
            )}
          </div>
        </div>

        <div className="chart-card insights">
          <div className="chart-header">
            <h3>Key Insights</h3>
            <p>AI-powered business recommendations</p>
          </div>
          <div className="insights-content">
            <div className="insight-item positive">
              <div className="insight-icon">📈</div>
              <div className="insight-text">
                <strong>Revenue Growth:</strong> Your revenue has increased by {analytics.monthlyGrowth}% this month
              </div>
            </div>
            <div className="insight-item neutral">
              <div className="insight-icon">👥</div>
              <div className="insight-text">
                <strong>Student Retention:</strong> {analytics.activeStudents} active students with good engagement
              </div>
            </div>
            <div className="insight-item recommendation">
              <div className="insight-icon">💡</div>
              <div className="insight-text">
                <strong>Recommendation:</strong> Consider raising prices by 10% - demand supports it
              </div>
            </div>
            <div className="insight-item warning">
              <div className="insight-icon">⚠️</div>
              <div className="insight-text">
                <strong>Optimization:</strong> Schedule utilization at {analytics.scheduleUtilization}% - aim for 80-90%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Student Analytics Section */}
      <div className="student-analytics-section">
        <div className="section-header">
          <h2>Student Analytics</h2>
          <p>Deep insights into student retention, progress, and engagement</p>
        </div>

        <div className="student-metrics-grid">
          <div className="metric-card retention">
            <div className="metric-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
              </svg>
            </div>
            <div className="metric-content">
              <h3>Retention Rate</h3>
              <div className="metric-value">{studentAnalytics.retentionRate}%</div>
              <div className="metric-subtitle">Students staying active</div>
            </div>
          </div>

          <div className="metric-card progress">
            <div className="metric-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/>
              </svg>
            </div>
            <div className="metric-content">
              <h3>Average Progress</h3>
              <div className="metric-value">{studentAnalytics.averageProgress}</div>
              <div className="metric-subtitle">Lessons per student</div>
            </div>
          </div>

          <div className="metric-card engagement">
            <div className="metric-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4"/>
                <path d="M21 12c-1 0-2-1-2-2s1-2 2-2 2 1 2 2-1 2-2 2z"/>
                <path d="M3 12c1 0 2-1 2-2s-1-2-2-2-2 1-2 2 1 2 2 2z"/>
                <path d="M12 3c0 1-1 2-2 2s-2-1-2-2 1-2 2-2 2 1 2 2z"/>
                <path d="M12 21c0-1 1-2 2-2s2 1 2 2-1 2-2 2-2-1-2-2z"/>
              </svg>
            </div>
            <div className="metric-content">
              <h3>Engagement Score</h3>
              <div className="metric-value">{studentAnalytics.engagementMetrics.satisfactionScore}/5</div>
              <div className="metric-subtitle">Student satisfaction</div>
            </div>
          </div>

          <div className="metric-card completion">
            <div className="metric-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22,4 12,14.01 9,11.01"/>
              </svg>
            </div>
            <div className="metric-content">
              <h3>Completion Rate</h3>
              <div className="metric-value">{studentAnalytics.engagementMetrics.completionRate}%</div>
              <div className="metric-subtitle">Lessons completed</div>
            </div>
          </div>
        </div>

        <div className="student-details-grid">
          <div className="detail-card top-students">
            <div className="card-header">
              <h3>Top Performing Students</h3>
              <p>Most active students this month</p>
            </div>
            <div className="students-list">
              {studentAnalytics.topStudents.map((student, index) => (
                <div key={student.id} className="student-item">
                  <div className="student-rank">#{index + 1}</div>
                  <div className="business-student-info">
                    <div className="business-student-name">{student.name}</div>
                    <div className="business-student-stats">
                      {student.recentActivity} lessons • {formatCurrency(student.price)}/lesson
                    </div>
                  </div>
                  <div className="student-status active">Active</div>
                </div>
              ))}
            </div>
          </div>

          <div className="detail-card at-risk">
            <div className="card-header">
              <h3>Students at Risk</h3>
              <p>Students who need attention</p>
            </div>
            <div className="students-list">
              {studentAnalytics.atRiskStudents.slice(0, 3).map((student) => (
                <div key={student.id} className="student-item">
                  <div className="student-avatar">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </div>
                  <div className="business-student-info">
                    <div className="business-student-name">{student.name}</div>
                    <div className="business-student-stats">
                      {student.daysSinceLastLesson} days since last lesson
                    </div>
                  </div>
                  <div className={`student-status ${student.status}`}>
                    {student.status === 'at_risk' ? 'At Risk' : 'Needs Attention'}
                  </div>
                </div>
              ))}
              {studentAnalytics.atRiskStudents.length === 0 && (
                <div className="no-students">
                  <p>No students at risk! 🎉</p>
                </div>
              )}
            </div>
          </div>

          <div className="detail-card segments">
            <div className="card-header">
              <h3>Student Segments</h3>
              <p>Breakdown by proficiency level</p>
            </div>
            <div className="segments-list">
              {Object.entries(studentAnalytics.studentSegments).map(([level, data]) => (
                <div key={level} className="segment-item">
                  <div className="segment-header">
                    <span className="segment-level">{level}</span>
                    <span className="segment-count">{data.count} students</span>
                  </div>
                  <div className="segment-stats">
                    <div className="stat">
                      <span className="stat-label">Active:</span>
                      <span className="stat-value">{data.active}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Revenue:</span>
                      <span className="stat-value">{formatCurrency(data.revenue)}</span>
                    </div>
                  </div>
                  <div className="segment-progress">
                    <div 
                      className="progress-bar" 
                      style={{ width: `${(data.active / data.count) * 100}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* AI Insights Section */}
      <section className="ai-insights-section">
        <h2>AI Recommendations & Optimization</h2>
        
        <div className="ai-insights-grid">
          {/* AI Recommendations */}
          <div className="ai-card recommendations">
            <div className="ai-card-header">
              <h3>🤖 AI Recommendations</h3>
              <span className="ai-badge">AI</span>
            </div>
            <div className="recommendations-list">
              {aiInsights.recommendations.map((rec, index) => (
                <div key={index} className="recommendation-item">
                  <div className="recommendation-header">
                    <h4>{rec.title}</h4>
                    <span className={`impact-badge ${rec.impact}`}>
                      {rec.impact === 'high' ? 'High' : rec.impact === 'medium' ? 'Medium' : 'Low'}
                    </span>
                  </div>
                  <p>{rec.description}</p>
                  <button className="action-button">{rec.action}</button>
                </div>
              ))}
            </div>
          </div>

          {/* Alerts and Notifications */}
          <div className="ai-card alerts">
            <div className="ai-card-header">
              <h3>⚠️ Alerts</h3>
              <span className="alerts-count">{aiInsights.alerts.length}</span>
            </div>
            <div className="alerts-list">
              {aiInsights.alerts.map((alert, index) => (
                <div key={index} className={`alert-item ${alert.type}`}>
                  <div className="alert-icon">
                    {alert.type === 'warning' ? '⚠️' : 'ℹ️'}
                  </div>
                  <div className="alert-content">
                    <h4>{alert.title}</h4>
                    <p>{alert.description}</p>
                  </div>
                  <div className={`priority-indicator ${alert.priority}`}></div>
                </div>
              ))}
            </div>
          </div>

          {/* Business Opportunities */}
          <div className="ai-card opportunities">
            <div className="ai-card-header">
              <h3>💡 Growth Opportunities</h3>
              <span className="opportunities-count">{aiInsights.opportunities.length}</span>
            </div>
            <div className="opportunities-list">
              {aiInsights.opportunities.map((opp, index) => (
                <div key={index} className="opportunity-item">
                  <div className="opportunity-header">
                    <h4>{opp.title}</h4>
                    <span className="potential-badge">{opp.potential}</span>
                  </div>
                  <p>{opp.description}</p>
                  <div className="opportunity-actions">
                    <button className="primary-button">Explore</button>
                    <button className="secondary-button">Later</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Trend Analysis */}
          <div className="ai-card trends">
            <div className="ai-card-header">
              <h3>📊 Trend Analysis</h3>
              <span className="trends-period">This month</span>
            </div>
            <div className="trends-list">
              {aiInsights.trends.map((trend, index) => (
                <div key={index} className="trend-item">
                  <div className="trend-info">
                    <h4>{trend.title}</h4>
                    <span className="trend-period">{trend.period}</span>
                  </div>
                  <div className={`trend-value ${trend.trend}`}>
                    <span className="trend-icon">
                      {trend.trend === 'up' ? '↗️' : '↘️'}
                    </span>
                    <span className="trend-number">{trend.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Optimization Suggestions */}
          <div className="ai-card optimization">
            <div className="ai-card-header">
              <h3>⚡ Optimization</h3>
              <span className="optimization-badge">Auto</span>
            </div>
            <div className="optimization-suggestions">
              <div className="optimization-item">
                <div className="optimization-icon">📅</div>
                <div className="optimization-content">
                  <h4>Schedule optimization</h4>
                  <p>Move 3 lessons to more popular time slots</p>
                  <div className="optimization-benefit">
                    <span>Potential growth: +15% utilization</span>
                  </div>
                </div>
                <button className="apply-button">Apply</button>
              </div>
              
              <div className="optimization-item">
                <div className="optimization-icon">💰</div>
                <div className="optimization-content">
                  <h4>Dynamic pricing</h4>
                  <p>Increase prices by 10% for new students</p>
                  <div className="optimization-benefit">
                    <span>Potential growth: +8% revenue</span>
                  </div>
                </div>
                <button className="apply-button">Apply</button>
              </div>
              
              <div className="optimization-item">
                <div className="optimization-icon">🎯</div>
                <div className="optimization-content">
                  <h4>Personalization</h4>
                  <p>Set up individual programs for 5 students</p>
                  <div className="optimization-benefit">
                    <span>Potential growth: +20% retention</span>
                  </div>
                </div>
                <button className="apply-button">Apply</button>
              </div>
            </div>
          </div>

          {/* Predictive Analytics */}
          <div className="ai-card predictive">
            <div className="ai-card-header">
              <h3>🔮 Predictions</h3>
              <span className="predictive-badge">ML</span>
            </div>
            <div className="predictive-insights">
              <div className="prediction-item">
                <div className="prediction-header">
                  <h4>Revenue forecast</h4>
                  <span className="prediction-confidence">95%</span>
                </div>
                <div className="prediction-values">
                  <div className="prediction-current">
                    <span>Current month</span>
                    <strong>₽{Math.round(analytics.totalRevenue * 0.8).toLocaleString()}</strong>
                  </div>
                  <div className="prediction-next">
                    <span>Next month</span>
                    <strong>₽{Math.round(analytics.totalRevenue * 1.15).toLocaleString()}</strong>
                  </div>
                </div>
                <div className="prediction-trend positive">
                  <span>Expected growth: +15%</span>
                </div>
              </div>
              
              <div className="prediction-item">
                <div className="prediction-header">
                  <h4>Student forecast</h4>
                  <span className="prediction-confidence">87%</span>
                </div>
                <div className="prediction-values">
                  <div className="prediction-current">
                    <span>Current month</span>
                    <strong>{analytics.activeStudents}</strong>
                  </div>
                  <div className="prediction-next">
                    <span>Next month</span>
                    <strong>{analytics.activeStudents + 3}</strong>
                  </div>
                </div>
                <div className="prediction-trend positive">
                  <span>Expected growth: +3 students</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
